/**
 * Fog-of-war memory. Tracks which tiles the player has ever seen (permanent
 * bitmap) and which are currently visible (per-tick Set). Also updates each
 * guard's `lastSeen{X,Y,glyph,fg}` snapshot while in the player's cone so
 * the renderer can draw a ghost at the last-observed tile once contact is
 * broken. Headless — no PixiJS.
 */

import type { Guard } from './guard';
import type { MapData } from './map';

export class FovMemory {
  /** 1 iff the tile has been in the player's FOV at any point. */
  readonly seen: Uint8Array;
  /** Snapshot of the player's FOV for THIS tick. Replaced each update. */
  currentlyVisible: Set<number> = new Set();

  constructor(width: number, height: number) {
    this.seen = new Uint8Array(width * height);
  }

  /** Fold a fresh FOV set into memory; mark every tile in it as seen. */
  update(newFov: Set<number>): void {
    this.currentlyVisible = newFov;
    for (const idx of newFov) this.seen[idx] = 1;
  }

  isCurrentlyVisible(idx: number): boolean {
    return this.currentlyVisible.has(idx);
  }
  isSeen(idx: number): boolean {
    return this.seen[idx] === 1;
  }

  /** Reset everything (used by hard-reset / respawn). */
  reset(): void {
    this.seen.fill(0);
    this.currentlyVisible = new Set();
  }
}

/** Update each guard's last-seen snapshot for the tiles currently visible. */
export function updateGuardMemory(
  guards: readonly Guard[],
  playerFov: Set<number>,
  map: MapData,
): void {
  for (const g of guards) {
    const idx = g.y * map.width + g.x;
    if (playerFov.has(idx)) {
      g.everSeenByPlayer = true;
      g.lastSeenX = g.x;
      g.lastSeenY = g.y;
      g.lastSeenGlyph = g.glyph;
      g.lastSeenFg = g.fg;
    }
  }
}
